/**
 * WebSocket Context Provider
 * Provides a single shared WebSocket connection for the entire app
 * Prevents multiple connections from being created
 */

import React, { createContext, ReactNode, useContext } from 'react';
import { useWebSocketOrders } from '../hooks/useWebSocketOrders';

interface Order {
  id: number;
  type: string;
  stock: string;
  hedge: string;
  hedgeStrike: number;
  strike: number;
  otype: string;
  trend: string;
  entryLogic: string;
  exitLogic?: string;
  logicCondition: string;
  logicSpotPrice: number;
  orderSide: string;
  qtySize: number;
  limitPrice: number;
  orderType: string;
  productType: string;
  slCheck: string;
  slPrice: number;
  tgCheck: string;
  tgPrice: number;
  status: string;
  exitType?: string;
  created_at: string;
  updated_at: string | null;
}

interface OrderBookItem {
  uid: number;
  guid: string;
  order_id: string;
  order_unique_identifier: string;
  client_id: string;
  instrument_identifier: string;
  exchange_instrument_id: number;
  atp: number;
  trade_type: string;
  order_status: string;
  order_side: string;
  order_generated_time: string;
  exchange_transact_time: string;
  quantity: number;
  limit_price: number;
  stop_price: number;
  order_type: string;
  last_updated_time: string;
  pdt_type: string;
  order_reject_reason: string;
  stock: string;
  order_date: string;
  lot_size: number;
  is_order_completed: boolean;
  is_order_placed: boolean;
  exchange_transaction_orderid: string;
  exchange_segment: string;
  generated_by: string;
  order_added_time: string;
  order_update_source: string;
  retry_count: number;
  broker_name: string;
  position_update: boolean;
}

interface PositionItem {
  uid: number;
  instrument: string;
  instrument_id: number;
  buy_price: number | null;
  buy_qty: number | null;
  sell_price: number | null;
  sell_qty: number | null;
  net_qty: number | null;
  ltp: number | null;
  gross_pnl: number | null;
  order_list: any;
  created_at: string;
  updated_at: string;
}

interface WebSocketContextType {
  orders: Order[];
  orderBook: OrderBookItem[];
  positions: PositionItem[];
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
  sendJson?: (msg: any) => void;
  liveLTPMap: Map<number, number>;
  onMarketData: React.MutableRefObject<((message: any) => void) | null>;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * WebSocket Provider Component
 * Wraps the app and provides a single WebSocket connection
 *
 * PERFORMANCE OPTIMIZATION:
 * - useWebSocketOrders hook returns memoized object with stable reference
 * - Context value only changes when actual data (orders/orderBook/positions) changes
 * - Callbacks (reconnect, sendJson) are memoized with useCallback in the hook
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  // Single WebSocket connection for entire app
  // Hook returns memoized object, so this reference is stable unless data changes
  const websocket = useWebSocketOrders();

  // Simply pass through the already-memoized websocket object
  // No need for additional useMemo since hook handles memoization
  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket connection from anywhere in the app
 * Ensures all components share the same connection
 */
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);

  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }

  return context;
};
